import {
    createApi,
    fetchBaseQuery
} from "@reduxjs/toolkit/query/react";
import { RootState } from '../'
import {
    setIsLoading, setOauthToken,
    setResults, setFirstAttachment,
    setPreviousAttachmentId,
    setIsConversationOpen,
    setLoadingMessage,
    setSkipAttachmentPolling,
    setConversationId,
    setMessageId,
    setSpaceId,
    selectSpaceId,
    selectConversationId,
} from '../features/genieSlice'
import { chatLog } from './chatlog'
import { useSelector } from "react-redux";

const BASE_URL = "https://eudtuapimgmd01.azure-api.net/genie/spaces/"

interface ConversationParams {
    content: string
}

// Custom baseQuery that handles workspace ID
const baseQueryWithWorkspace = async (args: any, api: any, extraOptions: any) => {
    const state = api.getState() as RootState
    const workspaceId = state.genie.workspaceId
    
    // Only prepend workspace ID for relative URLs, not for full URLs like getToken
    if (args.url && !args.url.startsWith('http') && workspaceId) {
        args.url = `${workspaceId}${args.url}`
    }
    
    return fetchBaseQuery({
        baseUrl: BASE_URL,
        prepareHeaders: async (headers) => {
            headers.set('Content-Type', `application/json`)
            headers.set('Ocp-Apim-Subscription-Key', `5ca9823e808841d0aa78f7964b910bd8`)

            // Set authorization header based on available tokens
            if (state.genie.oauthToken) {
                headers.set('Authorization', `Bearer ${state.genie.oauthToken}`)
            }
            else if (state.auth.idToken) {
                headers.set('Authorization', `Bearer ${state.auth.idToken}`)
            }

            return headers
        }
    })(args, api, extraOptions)
}

export const genieApi = createApi({
    reducerPath: "genieApi",
    baseQuery: baseQueryWithWorkspace,
    tagTypes: ["Genie", "Attachments"],
    endpoints: (builder) => ({
        getToken: builder.query({
            query: (_: {}) => ({
                url: `https://eudtuapimgmd01.azure-api.net/genie-login/token`,
                method: 'POST'
            }),
            async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
                dispatch(setIsLoading(true))
                try {
                    const { data } = await queryFulfilled
                    dispatch(setOauthToken(data.access_token))
                } catch (err) {
                }
                dispatch(setIsLoading(false))
            }
        }),
        startConversation: builder.mutation({
            query: (params: ConversationParams) => ({
                url: `/start-conversation`,
                method: 'POST',
                body: params
            }),
            async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
                dispatch(setIsLoading(true))
                dispatch(setLoadingMessage("starting conversation..."))
                try {
                    const { data } = await queryFulfilled
                    dispatch(setConversationId(data.conversation_id))
                    dispatch(setSpaceId(data.message.space_id))
                    dispatch(setMessageId(data.message_id))
                    // dispatch(setConversationPayload(data))
                } catch (err) {
                    console.error("Error in startConversation:", err)
                    dispatch(setSkipAttachmentPolling(true))
                }
                dispatch(setSkipAttachmentPolling(false))
                dispatch(setIsLoading(false))
            }
        }),
        getAttachments: builder.query({
            query: ({ conversation_id, message_id }: any) => ({
                url: `/conversations/${conversation_id}/messages/${message_id}`,
                method: 'GET',
            }),
            providesTags: ['Attachments'],
            async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
                dispatch(setIsLoading(true))
                const state = getState() as RootState
                const previousAttachmentId = state.genie.previousAttachmentId
                const convid = selectConversationId(state)
                const spaceid = selectSpaceId(state)

                try {
                    const { data } = await queryFulfilled

                    dispatch(setLoadingMessage(data.status))
                    if (['COMPLETED', 'FAILED'].includes(data.status)) {
                        // Abort any ongoing polling requests
                        if (data?.attachments?.[0]?.query  && previousAttachmentId !== data?.attachments?.[0]?.attachment_id ) {
                            dispatch(setFirstAttachment(data?.attachments?.[0]));
                            dispatch(setPreviousAttachmentId(data?.attachments?.[0]?.attachment_id))
                            // Stop polling by invalidating the query cache
                            dispatch(genieApi.util.invalidateTags(['Attachments']));
                            dispatch(setMessageId(''))
                            dispatch(setIsLoading(false))
                        } else if (data?.attachments?.[0]?.text) {
                            dispatch(setResults({
                                ...data?.attachments?.[0],
                                question: data?.content,
                                type: 'content',
                                status: 'COMPLETED'
                            }));

                            dispatch(chatLog.endpoints.genieLog.initiate({
                                email: state.auth.userId,
                                question: data?.content,
                                sql: 'not available',
                                response: JSON.stringify(data?.attachments?.[0]?.text || []),
                                source: 'api-call',
                                sql_explanation: 'not available',
                                conversation_id: convid,
                                space_id: spaceid
                            }))

                            dispatch(setSkipAttachmentPolling(true))
                            dispatch(setFirstAttachment(null));
                            // dispatch(setFollowup(null))
                            dispatch(setMessageId(''))
                            dispatch(setIsLoading(false))
                            dispatch(setIsConversationOpen(false))
                        } else if (data?.attachments?.[0]?.error) {
                            dispatch(setResults({
                                ...data,
                                question: data?.content,
                                type: 'content',
                                status: 'FAILED'
                            }));

                            dispatch(setSkipAttachmentPolling(true))
                            dispatch(setFirstAttachment(null));
                            // dispatch(setFollowup(null))
                            dispatch(setMessageId(''))
                            dispatch(setIsLoading(false))
                            dispatch(setIsConversationOpen(false))
                        }
                    }
                } catch (err) {
                    console.error("Error in startConversation:", err)
                    dispatch(setIsLoading(false))
                }
            }
        }),
        queryResults: builder.query({
            query: ({ conversation_id, message_id, attachment_id }: any) => ({
                url: `/conversations/${conversation_id}/messages/${message_id}/attachments/${attachment_id}/query-result`,
                method: 'GET',
            }),
            async onQueryStarted({ attachment }, { getState, dispatch, queryFulfilled }) {
                const state = getState() as RootState
                const question = state.genie.question
                const convid = selectConversationId(state)
                const spaceid = selectSpaceId(state)

                dispatch(setIsLoading(true))

                try {
                    const { data } = await queryFulfilled
                    dispatch(setLoadingMessage(""))
                    if (data?.statement_response && question && attachment && data.statement_response.status.state === "SUCCEEDED") {
                        // Check if result has any meaningful data
                        const hasData = data.statement_response.result.data_array && 
                                       data.statement_response.result.data_array.length > 0 &&
                                       data.statement_response.result.data_array.some((row: any) => 
                                           row && row.some((cell: any) => cell !== null && cell !== undefined)
                                       );
                        
                        if (!hasData) {
                            dispatch(setResults({
                                ...data,
                                question,
                                type: 'content',
                                content: question,
                                attachment,
                                text: { content: "No data available." },
                                status: 'COMPLETED'
                            }));
                            dispatch(setMessageId(''))
                        } else {
                            dispatch(setResults({
                                ...data,
                                question,
                                type: 'response',
                                attachment,
                                status: 'COMPLETED'
                            }));

                        }
                        
                        dispatch(chatLog.endpoints.genieLog.initiate({
                            email: state.auth.userId,
                            question,
                            sql: attachment?.query?.query,
                            response: JSON.stringify(data?.statement_response?.result?.data_array || []),
                            source: 'api-call',
                            sql_explanation: attachment?.query?.description,
                            conversation_id: convid,
                            space_id: spaceid
                        }))

                        dispatch(setSkipAttachmentPolling(true))
                        dispatch(setFirstAttachment(null))
                        // dispatch(setFollowup(null))
                        dispatch(setMessageId(''))
                        // Stop getAttachments polling by invalidating the query cache
                        dispatch(genieApi.util.invalidateTags(['Attachments']));
                        // dispatch(setIsLoading(false))
                        dispatch(setIsConversationOpen(false))
                    }
                } catch (err) {
                    console.error("Error in query:", err)
                    dispatch(setMessageId(''))
                }
                dispatch(setIsLoading(false))
            }
        }),
        followUp: builder.mutation({
            query: ({ conversation_id, content }: any) => ({
                url: `/conversations/${conversation_id}/messages`,
                method: 'POST',
                body: {
                    content: content
                }
            }),
            async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
                dispatch(setIsLoading(true))
                try {
                    const { data } = await queryFulfilled
                    // dispatch(setFollowup(data))
                    dispatch(setConversationId(data.conversation_id))
                    dispatch(setMessageId(data.message_id))
                } catch (err) {
                    console.error("Error in followUp:", err)
                    dispatch(setSkipAttachmentPolling(true))
                }
                dispatch(setSkipAttachmentPolling(false))
                dispatch(setIsLoading(false))
            }
        })
    })
})

export const {
    useGetTokenQuery,
    useStartConversationMutation,
    useGetAttachmentsQuery,
    useQueryResultsQuery,
    useFollowUpMutation
} = genieApi;
